/**
 * The dashboards' wire layer — v19 D3 (the country dashboard + the group view).
 *
 * TRANSCRIBED FROM THE SERVICE, NOT GUESSED: `CountryDashboardDto` mirrors
 * `apps/api/src/dashboards/dashboard/dto/country-dashboard.dto.ts` field for field, and
 * `GroupDashboardDto` + `FxGateDto` mirror `dto/group-dashboard.dto.ts` and `fx-gate.ts`.
 *
 * ===========================================================================
 * MONEY CROSSES THIS BOUNDARY, MASKED PER COUNTRY — AND ONE KEY IS GATED TWICE
 * ===========================================================================
 * `grand_total`, `amount` and `total_inr` are registered money names; a seat without
 * `amounts.view` in a node's `country_code` receives the literal `'•••'` there, never a
 * number. Every consumer goes through `ServerAmount` / `formatMoney`, which ask
 * `isMaskedAmount` before parsing (`Number('•••')` is NaN, and a dashboard that rendered
 * NaN — or 0 — would tell a manager the pipeline is empty).
 *
 * `consolidated` is the ONE cross-currency figure in the product and it is gated on the
 * wire: it is `null` whenever `fx.status === 'unverified'`, with `fx.unverified[]` NAMING
 * the placeholder pairs and `fx.cleared_by` naming who clears them. The screen renders the
 * gate as a STATE, not an error, and renders no figure — `GroupDashboardPage.tsx`.
 *
 * Sections of the country dashboard are ABSENT (not zeroed) for a seat without that
 * section's key — the `/stats` composition idiom. An absent section is not an empty one.
 */

import { ApiRequestError, ApiTransportError, apiRequest } from '../../lib/api';
import type { PeriodQuery } from './periods';

/* ────────────────────────────────────────────────────── country dashboard DTOs ── */

export interface PeriodWindowDto {
  readonly from: string;
  readonly to: string;
  readonly from_date: string;
  readonly to_date: string;
}
export interface ResolvedPeriodDto extends PeriodWindowDto {
  readonly key: string;
  readonly tz: string;
}

export interface FlowCountDto {
  readonly count: number;
  readonly previous: number;
  readonly delta: number;
}
export interface InquiriesSectionDto {
  readonly by_stage: readonly { stage: string; count: number }[];
  readonly created: FlowCountDto;
}

export interface CurrencyTotalDto {
  readonly currency: string;
  readonly count: number;
  /** MONEY — `'•••'` for a masked seat. */
  readonly grand_total: string;
}
export interface PipelineStageDto {
  readonly stage: string;
  readonly count: number;
  readonly country_code: string;
  readonly by_currency: readonly CurrencyTotalDto[];
}
export interface ConversionCountsDto {
  readonly quotes_sent: number;
  readonly orders_confirmed: number;
  readonly rate_pct: number | null;
}
export interface ConversionDto extends ConversionCountsDto {
  readonly previous: ConversionCountsDto;
  readonly delta_pct: number | null;
}
export interface PipelineSectionDto {
  readonly by_stage: readonly PipelineStageDto[];
  readonly conversion: ConversionDto;
}

export interface InFlightCurrencyDto {
  readonly currency: string;
  readonly count: number;
  /** MONEY — `'•••'` for a masked seat. */
  readonly amount: string;
}
export interface InFlightDto {
  readonly count: number;
  readonly country_code: string;
  readonly by_currency: readonly InFlightCurrencyDto[];
}
export interface WorkSectionDto {
  readonly by_column: readonly { column: string; count: number }[];
  readonly in_flight: InFlightDto;
  readonly stuck: number;
  readonly stuck_after_days: number;
}
export interface SignoffsSectionDto {
  readonly waiting: number;
  readonly overdue: number;
  readonly overdue_after_days: number;
}
export interface RenewalsSectionDto {
  readonly due: number;
  readonly window: { readonly from: string; readonly to: string };
}

/** Optional keys are ABSENT per seat — never null, never zeroed. */
export interface CountryDashboardDto {
  readonly country_code: string;
  readonly currency: string;
  readonly today: string;
  readonly window: ResolvedPeriodDto;
  readonly previous: PeriodWindowDto;
  readonly inquiries?: InquiriesSectionDto;
  readonly pipeline?: PipelineSectionDto;
  readonly work?: WorkSectionDto;
  readonly signoffs?: SignoffsSectionDto;
  readonly renewals?: RenewalsSectionDto;
}

/* ─────────────────────────────────────────────────────────── group view DTOs ── */

export type FxGateStatus = 'verified' | 'unverified';

export interface FxPairStatus {
  readonly from_ccy: string;
  readonly to_ccy: string;
  readonly verified: boolean;
  /** 'seed' | 'manual' | 'auto' | null — provenance, never a value. */
  readonly source: string | null;
  readonly period_used: string | null;
  readonly stale: boolean;
  readonly missing: boolean;
  /** The row's `updated_at` — how old the placeholder is; null when no row exists. */
  readonly since: string | null;
}

/** NO RATE VALUES ON THIS OBJECT — `dashboard.group` is not `config.global` (§8 #3). */
export interface FxGateDto {
  readonly status: FxGateStatus;
  readonly period: string;
  readonly pairs: readonly FxPairStatus[];
  /** NAMED, in order. Empty iff `status === 'verified'`. */
  readonly unverified: readonly string[];
  readonly cleared_by: { readonly permission: string; readonly route: string; readonly how: string };
}

export interface GroupCountryBlockDto {
  readonly country_code: string;
  readonly currency: string;
  readonly pipeline: { readonly by_stage: readonly PipelineStageDto[]; readonly conversion: ConversionDto };
  readonly work: { readonly in_flight: InFlightDto; readonly stuck: number };
  readonly signoffs: { readonly waiting: number; readonly overdue: number };
}

export interface ConsolidatedStageDto {
  readonly stage: string;
  readonly count: number;
  /** MONEY, INR, DECIMAL string — `'•••'` for a non-grp seat (no `country_code` ⇒ UNKNOWN masks). */
  readonly total_inr: string;
}
/** Present ONLY when every needed rate is verified. */
export interface ConsolidatedDto {
  readonly base_currency: 'INR';
  readonly by_stage: readonly ConsolidatedStageDto[];
  readonly in_flight: { readonly count: number; readonly total_inr: string };
  readonly rate_table: readonly { from_ccy: string; period_used: string | null; stale: boolean }[];
}

export interface GroupDashboardDto {
  readonly generated_at: string;
  readonly base_currency: 'INR';
  readonly window: ResolvedPeriodDto;
  readonly previous: PeriodWindowDto;
  readonly fx: FxGateDto;
  readonly countries: readonly GroupCountryBlockDto[];
  /** `null` iff `fx.status === 'unverified'` — there is no partially-filled branch. */
  readonly consolidated: ConsolidatedDto | null;
}

/* ───────────────────────────────────────────────────────────────── fetchers ── */

/** `?period=&from=&to=` pass through UNVALIDATED — the server's 422 names the vocabulary. */
export async function fetchCountryDashboard(
  countryCode: string,
  query: PeriodQuery,
  signal?: AbortSignal,
): Promise<CountryDashboardDto> {
  return apiRequest<CountryDashboardDto>(`/countries/${countryCode}/dashboard`, {
    query: { period: query.period, from: query.from, to: query.to },
    signal,
  });
}

/** A GLOBAL route — no `:cc`; the seat's `dashboard.group` is the whole gate. */
export async function fetchGroupDashboard(
  query: PeriodQuery,
  signal?: AbortSignal,
): Promise<GroupDashboardDto> {
  return apiRequest<GroupDashboardDto>('/dashboard/group', {
    query: { period: query.period, from: query.from, to: query.to },
    signal,
  });
}

/* ────────────────────────────────────────────────────────────────── errors ── */

/** Re-authored per feature, the house pattern: a 403 never names the permission key. */
export function dashboardErrorText(error: unknown): string {
  if (error instanceof ApiTransportError) {
    return 'The server could not be reached. Nothing was changed.';
  }
  if (error instanceof ApiRequestError) {
    if (error.code === 'COUNTRY_SCOPE_VIOLATION') return 'That dashboard belongs to another country.';
    if (error.status === 403) return 'This account is not allowed to see that.';
    return error.message;
  }
  return 'Something went wrong. Nothing was changed.';
}

/* ─────────────────────────────────────────────────────────────── query keys ── */

export const dashboardKeys = {
  country: (cc: string, query: PeriodQuery) => ['dashboards', cc, 'country', query] as const,
  group: (query: PeriodQuery) => ['dashboards', 'group', query] as const,
};
